import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';

import type { Filters, TaxonomyFilter, TaxonomyFilterWithCount } from '@/types/filters';
import type {
  PriceRangeMetric,
  SessionDurationMetric,
  TaxonomyMetric,
  TaxonomyTypeMetric,
  TimeRangeMetric,
} from '@/types/metrics';

import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

export const useMetricsStore = defineStore('metrics', () => {
  /* VARIABLES */
  const priceRange = ref<PriceRangeMetric>({ min: 0, max: 1500, count: 10 });
  const sessionDuration = ref<SessionDurationMetric>({ min: 0, max: 24 });
  const timeRanges = ref<Array<TimeRangeMetric>>([]);
  const baseMetrics = ref<Array<any>>([]);

  const themes = ref<Array<TaxonomyMetric>>([]);
  const types = ref<Array<TaxonomyTypeMetric>>([]);
  const places = ref<Array<TaxonomyMetric>>([]);
  const audiences = ref<Array<TaxonomyMetric>>([]);
  const internals = ref<Array<TaxonomyMetric>>([]);

  const taxonomyFilter = ref<TaxonomyFilter | null>(null);
  const taxonomyFilterWithCount = ref<TaxonomyFilterWithCount>();

  /* STORE IMPORTS */
  const productsStore = useProductsStore();
  const filtersStore = useFiltersStore();
  const globalConfigurationStore = useGlobalConfigurationStore();
  const { apiURL, globalConfiguration } = storeToRefs(globalConfigurationStore);

  /* METHODS */
  async function getBaseMetrics(filters: Filters) {
    const payloadFilters = JSON.parse(JSON.stringify(filters));
    payloadFilters.size = 0;
    try {
      const productDTOs = await productsStore.getProductsList(payloadFilters);
      baseMetrics.value = productDTOs.metrics;
    }
    catch (err) {
      console.error('Unable to fetch data', err);
    }
  }

  async function computeMetrics(productDTOs: any) {
    const { filters } = storeToRefs(filtersStore);
    const metricsList = productDTOs?.metrics ?? [];

    // 1. Single Pass Dictionary: Group metrics by name for O(1) lookups
    const metricsMap = new Map();
    for (let i = 0; i < metricsList.length; i++) {
      metricsMap.set(metricsList[i].metricName, metricsList[i]);
    }

    // 2. Direct assignments (No repeated iterations)
    if (metricsMap.has('priceFromWithSelection'))
      priceRange.value = metricsMap.get('priceFromWithSelection').value;
    if (metricsMap.has('sessionDuration'))
      sessionDuration.value = metricsMap.get('sessionDuration').value;
    if (metricsMap.has('timeRangeWithSelection'))
      timeRanges.value = metricsMap.get('timeRangeWithSelection').value;
    if (metricsMap.has('taxonomyThemes'))
      themes.value = metricsMap.get('taxonomyThemes').value;
    if (metricsMap.has('taxonomyAudiences'))
      audiences.value = metricsMap.get('taxonomyAudiences').value;
    if (metricsMap.has('taxonomyInternals'))
      internals.value = metricsMap.get('taxonomyInternals').value;

    // Handle overwrite logic cleanly
    if (metricsMap.has('taxonomyPlacesWithSelection')) {
      places.value = metricsMap.get('taxonomyPlacesWithSelection').value;
    }
    else if (metricsMap.has('taxonomyPlaces')) {
      places.value = metricsMap.get('taxonomyPlaces').value;
    }

    const countWithSelection = metricsMap.get('taxonomyFilterCountWithSelection');
    if (countWithSelection?.value?.length) {
      taxonomyFilterWithCount.value = countWithSelection.value[0];
    }
    else if (metricsMap.has('taxonomyFilterCount')) {
      taxonomyFilterWithCount.value = metricsMap.get('taxonomyFilterCount').value?.[0];
    }

    const typesMetrics = metricsMap.get('taxonomyTypes');
    const crossedTypes = metricsMap.get('crossedTaxonomyCount')?.value?.types ?? [];

    const taxonomyTypesList = await getTaxonomiesTypes();

    // Pre-compute maps to prevent nested O(N) loops during `.map()`
    const pictogramMap = new Map();
    for (const type of taxonomyTypesList) {
      pictogramMap.set(type.id, type.pictogram?.value ?? null);
    }

    if (typesMetrics != null) {
      // Convert selections to a Set for O(1) .has() checks instead of O(N) .includes()
      const currentSelectionArr = filters.value.selections.find((it: any) => it.filterProperties?.type === 'taxonomyTypes')?.selection ?? [];
      const selectionSet = new Set(currentSelectionArr);

      // Convert crossed types to a Map for O(1) extraction
      const crossedTypesMap = new Map();
      for (const type of crossedTypes) crossedTypesMap.set(type.id, type);

      const hasCrossedTypes = crossedTypes.length > 0;

      types.value = typesMetrics.value.map((it: any) => {
        const matchingType = crossedTypesMap.get(it.id);
        return {
          ...(matchingType || it),
          isMatching: hasCrossedTypes ? !!matchingType : true,
          isSelected: selectionSet.has(it.id),
          pictogram: pictogramMap.get(it.id) ?? null,
        };
      });
    }

    // 3. Search sorts optimization
    const searchPresent = filters.value.sorts.find((it: any) => it.type === 'search');
    if (searchPresent) {
      if (!baseMetrics.value.length) {
        // Use native structuredClone - much faster and memory efficient than JSON.parse/stringify
        const baseFilters = structuredClone(filters.value);
        baseFilters.sorts = baseFilters.sorts.filter((it: any) => it.type !== 'search');
        await getBaseMetrics(baseFilters);
      }

      // Index baseMetrics for O(1) lookup
      const baseMetricsMap = new Map();
      for (const metric of baseMetrics.value) baseMetricsMap.set(metric.metricName, metric);

      const baseThemesMetrics = baseMetricsMap.get('taxonomyThemes');
      if (baseThemesMetrics)
        themes.value = baseThemesMetrics.value;

      const baseTypesMetrics = baseMetricsMap.get('taxonomyTypes');
      if (baseTypesMetrics) {
        // Index typesMetrics.value to avoid nested `.find()`
        const currentTypesMap = new Map();
        if (typesMetrics?.value) {
          for (const type of typesMetrics!.value) currentTypesMap.set(type.id, type);
        }

        types.value = baseTypesMetrics.value.map((it: any) => {
          const matchingType = currentTypesMap.get(it.id);
          return {
            ...(matchingType || it),
            isMatching: !!matchingType,
            isSelected: false,
            pictogram: pictogramMap.get(it.id) ?? null,
          };
        });
      }
    }
  }

  async function getDistributorFilter() {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    headers.set('Authorization', `Bearer ${globalConfiguration.value?.apikey}`);
    const request = await fetch(`${apiURL.value}/distributor_taxonomies_filter/list`, { headers });
    const response = await request.json();
    taxonomyFilter.value = (Array.isArray(response.data) && response.data.length !== 0) ? response.data[0] : null;
  }

  async function getTaxonomiesTypes() {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    headers.set('Authorization', `Bearer ${globalConfiguration.value?.apikey}`);
    const request = await fetch(`${apiURL.value}/taxonomy/list?type=TYPES&limit=100`, { headers });
    const list = await request.json();
    return list.data;
  }

  return {
    priceRange,
    sessionDuration,
    timeRanges,
    themes,
    types,
    places,
    audiences,
    internals,
    taxonomyFilter,
    taxonomyFilterWithCount,
    baseMetrics,
    getBaseMetrics,
    computeMetrics,
    getDistributorFilter,
    getTaxonomiesTypes,
  };
});
